// BattleHeader - Sleeper-style battle header with scores and tug-of-war
// Shows avatars, points, session progress, and bomb/bust counts

import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { HOLO_COLORS, GLOW_EFFECTS } from '../../constants/holoTheme';
import SessionHUD from './SessionHUD';
import FreeAgentBar from './FreeAgentBar';
import { transformSessionScores } from '../../utils/battleHelpers';

/**
 * Default avatar component when no image provided
 */
function DefaultAvatar({ name, isPlayer }) {
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  return (
    <div
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        backgroundColor: isPlayer ? HOLO_COLORS.cyan + '30' : HOLO_COLORS.purple + '30',
        border: `2px solid ${isPlayer ? HOLO_COLORS.cyan : HOLO_COLORS.purple}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        fontWeight: 700,
        color: isPlayer ? HOLO_COLORS.cyan : HOLO_COLORS.purple,
      }}
    >
      {initial}
    </div>
  );
}

DefaultAvatar.propTypes = {
  name: PropTypes.string,
  isPlayer: PropTypes.bool,
};

/**
 * PlayerSide - One side of the header (player or opponent)
 */
function PlayerSide({
  player,
  isRight = false,
  isLeading = false,
  battleVersion = 3,
}) {
  const {
    username = 'Player',
    avatar,
    totalPoints = 0,
    sessionPoints = 0,
    baggerBombs = 0,
    busts = 0,
  } = player;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isRight ? 'row-reverse' : 'row',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
      }}
    >
      {/* Avatar */}
      {avatar ? (
        <img
          src={avatar}
          alt={username}
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `2px solid ${isLeading ? HOLO_COLORS.green : HOLO_COLORS.borderSubtle}`,
            boxShadow: isLeading ? GLOW_EFFECTS.green : 'none',
          }}
        />
      ) : (
        <DefaultAvatar name={username} isPlayer={!isRight} />
      )}

      {/* Score Info */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isRight ? 'flex-end' : 'flex-start',
        }}
      >
        {/* Total Points */}
        <div
          style={{
            fontSize: '28px',
            fontWeight: 700,
            color: HOLO_COLORS.textPrimary,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {Math.round(totalPoints)}
        </div>

        {/* Session Points (V3 only — V4 has no sessions) */}
        {battleVersion < 4 && (
          <div
            style={{
              fontSize: '13px',
              color: sessionPoints >= 0 ? HOLO_COLORS.green : HOLO_COLORS.red,
              fontWeight: 500,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {sessionPoints >= 0 ? '+' : ''}{Math.round(sessionPoints)} session
          </div>
        )}

        {/* Username */}
        <div
          style={{
            fontSize: '11px',
            color: HOLO_COLORS.textMuted,
            marginTop: '2px',
          }}
        >
          {username}
        </div>
      </div>
    </div>
  );
}

PlayerSide.propTypes = {
  player: PropTypes.shape({
    id: PropTypes.string,
    username: PropTypes.string,
    avatar: PropTypes.string,
    totalPoints: PropTypes.number,
    sessionPoints: PropTypes.number,
    baggerBombs: PropTypes.number,
    busts: PropTypes.number,
  }).isRequired,
  isRight: PropTypes.bool,
  isLeading: PropTypes.bool,
};

/**
 * TugOfWarBeam - Animated score balance indicator
 */
function TugOfWarBeam({ playerPoints, opponentPoints }) {
  const total = playerPoints + opponentPoints;
  const playerPercent = total > 0 ? (playerPoints / total) * 100 : 50;

  return (
    <div
      style={{
        position: 'relative',
        height: '8px',
        backgroundColor: HOLO_COLORS.bgCard,
        borderRadius: '4px',
        overflow: 'hidden',
        border: `1px solid ${HOLO_COLORS.borderSubtle}`,
      }}
    >
      {/* Player side (cyan) */}
      <motion.div
        animate={{ width: `${playerPercent}%` }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          background: `linear-gradient(90deg, ${HOLO_COLORS.cyan}80, ${HOLO_COLORS.cyan})`,
          borderRadius: '4px 0 0 4px',
        }}
      />

      {/* Opponent side (purple) */}
      <motion.div
        animate={{ width: `${100 - playerPercent}%` }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          background: `linear-gradient(90deg, ${HOLO_COLORS.purple}, ${HOLO_COLORS.purple}80)`,
          borderRadius: '0 4px 4px 0',
        }}
      />

      {/* Center marker (needle) */}
      <motion.div
        animate={{ left: `${playerPercent}%` }}
        transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        style={{
          position: 'absolute',
          top: '50%',
          width: '12px',
          height: '12px',
          marginLeft: '-6px',
          marginTop: '-6px',
          backgroundColor: HOLO_COLORS.textPrimary,
          borderRadius: '50%',
          boxShadow: '0 0 8px rgba(255,255,255,0.5)',
          zIndex: 10,
        }}
      />
    </div>
  );
}

TugOfWarBeam.propTypes = {
  playerPoints: PropTypes.number.isRequired,
  opponentPoints: PropTypes.number.isRequired,
};

/**
 * BombBustCounts - Display bomb and bust counts
 */
function BombBustCounts({ player, opponent }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '12px',
        color: HOLO_COLORS.textMuted,
        padding: '0 4px',
      }}
    >
      <div style={{ display: 'flex', gap: '8px' }}>
        <span>
          💣 <span style={{ color: HOLO_COLORS.green }}>{player.baggerBombs || 0}</span>
        </span>
        <span>
          📉 <span style={{ color: HOLO_COLORS.red }}>{player.busts || 0}</span>
        </span>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <span>
          💣 <span style={{ color: HOLO_COLORS.green }}>{opponent.baggerBombs || 0}</span>
        </span>
        <span>
          📉 <span style={{ color: HOLO_COLORS.red }}>{opponent.busts || 0}</span>
        </span>
      </div>
    </div>
  );
}

BombBustCounts.propTypes = {
  player: PropTypes.shape({
    baggerBombs: PropTypes.number,
    busts: PropTypes.number,
  }).isRequired,
  opponent: PropTypes.shape({
    baggerBombs: PropTypes.number,
    busts: PropTypes.number,
  }).isRequired,
};

/**
 * SwapMarketButton - Animated button to open the Swap Market
 */
function SwapMarketButton({ swapsRemaining = 0, onClick, disabled = false }) {
  // Inject shimmer keyframe once
  useEffect(() => {
    const id = 'swap-market-btn-styles';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes swapShimmer {
        0% { left: -100%; }
        50% { left: 100%; }
        100% { left: 100%; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const hasSwaps = swapsRemaining > 0;

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.03 } : {}}
      whileTap={!disabled ? { scale: 0.97 } : {}}
      onClick={!disabled ? onClick : undefined}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '320px',
        margin: '0 auto',
        padding: '14px 24px',
        borderRadius: '14px',
        border: 'none',
        background: hasSwaps
          ? `linear-gradient(135deg, rgba(0,217,255,0.15) 0%, rgba(139,92,246,0.15) 50%, rgba(0,217,255,0.15) 100%)`
          : 'rgba(255,255,255,0.05)',
        boxShadow: hasSwaps
          ? `0 0 0 1px rgba(0,217,255,0.4), 0 0 20px rgba(0,217,255,0.1), 0 4px 12px rgba(0,0,0,0.3)`
          : `0 0 0 1px rgba(255,255,255,0.1), 0 2px 8px rgba(0,0,0,0.2)`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        overflow: 'hidden',
        transition: 'opacity 0.2s',
      }}
    >
      {/* Shimmer overlay */}
      {hasSwaps && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '60%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(0,217,255,0.08), transparent)',
            transform: 'skewX(-20deg)',
            animation: 'swapShimmer 3s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Label */}
      <span
        style={{
          fontSize: '15px',
          fontWeight: 700,
          color: hasSwaps ? HOLO_COLORS.textPrimary : HOLO_COLORS.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}
      >
        Swap Picks
      </span>

      {/* Counter Badge */}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          borderRadius: '16px',
          backgroundColor: hasSwaps
            ? 'rgba(0, 217, 255, 0.3)'
            : 'rgba(239, 68, 68, 0.3)',
          border: hasSwaps
            ? '1px solid rgba(0, 217, 255, 0.6)'
            : '1px solid rgba(239, 68, 68, 0.5)',
          color: hasSwaps ? '#00d9ff' : '#ef4444',
          fontSize: '16px',
          fontWeight: 900,
          padding: '0 10px',
        }}
      >
        {swapsRemaining}
      </span>
    </motion.button>
  );
}

SwapMarketButton.propTypes = {
  swapsRemaining: PropTypes.number,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
};

/**
 * BattleHeader - Main header component
 */
export default function BattleHeader({
  player,
  opponent,
  currentSession,
  sessionTimeRemaining,
  sessionScores,
  completedSessions,
  // V4 props (grouped)
  battleVersion = 3,
  freeAgentConfig = {},
  isTraining = false,
}) {
  // Destructure V4 swap props used locally (rest is spread to FreeAgentBar)
  const { swapMode, onCancelSwapMode, onEnterSwapMode, swapsRemaining } = freeAgentConfig;

  // Determine who is leading
  const playerLeading = player.totalPoints > opponent.totalPoints;
  const opponentLeading = opponent.totalPoints > player.totalPoints;

  const hudSessionScores = useMemo(
    () => transformSessionScores(sessionScores),
    [sessionScores]
  );

  return (
    <div
      style={{
        backgroundColor: 'transparent',
        padding: '16px',
        margin: '0 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* Title */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '8px',
        }}
      >
        <span
          style={{
            fontSize: typeof window !== 'undefined' && window.innerWidth >= 768 ? '20px' : '16px',
            fontWeight: 800,
            color: '#ffffff',
            textAlign: 'center',
            textShadow: '0 0 12px rgba(0, 217, 255, 0.5), 0 0 24px rgba(0, 217, 255, 0.2)',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
          }}
        >
          BaggerBomb Battle
        </span>
        {isTraining && (
          <span
            style={{
              backgroundColor: HOLO_COLORS.purple,
              color: '#ffffff',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Training
          </span>
        )}
      </div>

      {/* Score Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <PlayerSide
          player={player}
          isRight={false}
          isLeading={playerLeading}
          battleVersion={battleVersion}
        />

        {/* VS Badge */}
        <div
          style={{
            fontSize: '14px',
            fontWeight: 700,
            color: HOLO_COLORS.textMuted,
          }}
        >
          ⚔️
        </div>

        <PlayerSide
          player={opponent}
          isRight={true}
          isLeading={opponentLeading}
          battleVersion={battleVersion}
        />
      </div>

      {/* Tug-of-War Beam */}
      <TugOfWarBeam
        playerPoints={player.totalPoints || 0}
        opponentPoints={opponent.totalPoints || 0}
      />

      {/* Bomb/Bust Counts */}
      <BombBustCounts player={player} opponent={opponent} />

      {/* Unified Swap + Free Agent Card (V4+) */}
      {battleVersion >= 4 && (
        <div
          style={{
            backgroundColor: 'rgba(13, 17, 23, 0.55)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(0, 217, 255, 0.12)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {/* Swap Picks Button */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {swapMode?.active ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onCancelSwapMode}
                style={{
                  padding: '8px 20px',
                  borderRadius: '20px',
                  border: 'none',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.25), rgba(220, 38, 38, 0.15))',
                  color: HOLO_COLORS.red,
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: `0 0 15px rgba(239, 68, 68, 0.2), 0 2px 8px rgba(0, 0, 0, 0.3)`,
                  letterSpacing: '0.5px',
                }}
              >
                Cancel Swap
              </motion.button>
            ) : (
              <SwapMarketButton
                swapsRemaining={swapsRemaining}
                onClick={onEnterSwapMode}
                disabled={swapsRemaining <= 0}
              />
            )}
          </div>

          {/* Free Agent Bar */}
          <FreeAgentBar
            {...freeAgentConfig}
            hideSwapButton
          />
        </div>
      )}

      {/* V3: Session HUD */}
      {battleVersion < 4 && (
        <SessionHUD
          currentSession={currentSession}
          timeRemaining={sessionTimeRemaining}
          sessionScores={hudSessionScores}
          completedSessions={completedSessions || []}
        />
      )}
    </div>
  );
}

BattleHeader.propTypes = {
  /** Player data */
  player: PropTypes.shape({
    id: PropTypes.string,
    username: PropTypes.string,
    avatar: PropTypes.string,
    totalPoints: PropTypes.number,
    sessionPoints: PropTypes.number,
    baggerBombs: PropTypes.number,
    busts: PropTypes.number,
  }).isRequired,
  /** Opponent data */
  opponent: PropTypes.shape({
    id: PropTypes.string,
    username: PropTypes.string,
    avatar: PropTypes.string,
    totalPoints: PropTypes.number,
    sessionPoints: PropTypes.number,
    baggerBombs: PropTypes.number,
    busts: PropTypes.number,
  }).isRequired,
  /** Current active session (V3) */
  currentSession: PropTypes.oneOf(['MORNING_BELL', 'MIDDAY', 'POWER_HOUR', 'NIGHT_GAME', '']),
  /** Seconds remaining in current session (V3) */
  sessionTimeRemaining: PropTypes.number,
  /** Session scores object (V3) */
  sessionScores: PropTypes.object,
  /** Array of completed session keys (V3) */
  completedSessions: PropTypes.arrayOf(PropTypes.string),
  /** Battle version (3 or 4) */
  battleVersion: PropTypes.number,
  /** V4: Grouped free agent config (spread to FreeAgentBar) */
  freeAgentConfig: PropTypes.object,
  /** Whether this is a training battle */
  isTraining: PropTypes.bool,
};

BattleHeader.defaultProps = {
  currentSession: '',
  sessionTimeRemaining: 0,
  sessionScores: {},
  completedSessions: [],
  battleVersion: 3,
  freeAgentConfig: {},
  isTraining: false,
};
